/**
 * Permissions Matrix — Conformara RBAC (permissions-RBAC-PRD-v0.2 decision 14).
 *
 * The company Admin's visual permission chart: rows = permission keys grouped
 * by module (Users, Templates, Audits, Incidents, PPE, Equipment/Assets,
 * Fleet/DOT, JHA, Certs, ...), columns = ladder roles
 * (Owner, Admin, Safety, PM, CM, CS, Foreman, Worker, Read-only),
 * cells = ● / ◐ / — toggles.
 *
 * Ships in the "Conformara Standard" state. Admin can toggle any non-locked
 * cell (non-waivable safety invariants render a lock icon and are immutable),
 * name the result as a preset, and revert to Standard with one click.
 * Toggles persist server-side via /api/permissions/overrides (PRD §2.4) —
 * this page holds local pending state and commits on Save.
 *
 * SCAFFOLD STATE: rendering + pending-edit state + API wiring for reads are
 * implemented; the mutation handlers call the (currently 501) endpoints and
 * surface the server message. GLM worker completes save/revert against the
 * real handlers.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, RotateCcw, Save, ShieldCheck, Loader2 } from 'lucide-react';
import { api } from '@/utils/api';
import type {
  BaselineCell,
  MatrixRoleColumn,
  PermissionMatrixModule,
  PermissionMatrixResponse,
  PermissionMatrixMetaResponse,
  PermissionOverride,
} from '@/types/permissions';

// ── Display constants ────────────────────────────────────────────────────────

const CELL_GLYPH: Record<BaselineCell, string> = {
  FULL: '●',
  SCOPED: '◐',
  DELEGATABLE: 'D',
  DENIED: '—',
};

const CELL_CYCLE: Record<string, BaselineCell> = {
  // Toggle semantics (decision 14): ● -> ◐ -> — -> ● . 'D' cells toggle to ●.
  FULL: 'SCOPED',
  SCOPED: 'DENIED',
  DELEGATABLE: 'FULL',
  DENIED: 'FULL',
};

const CELL_TITLE: Record<BaselineCell, string> = {
  FULL: 'Granted company-wide',
  SCOPED: 'Granted within assigned scope (projects/objects)',
  DELEGATABLE: 'Not baseline — delegatable via grant',
  DENIED: 'Denied',
};

const COLUMN_LABELS: Record<MatrixRoleColumn, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  SAFETY_MANAGER: 'Safety',
  PROJECT_MANAGER: 'PM',
  CONSTRUCTION_MANAGER: 'CM',
  CONSTRUCTION_SUPERVISOR: 'CS',
  FOREMAN: 'Foreman',
  WORKER: 'Worker',
  READ_ONLY: 'Read-only',
};

type PendingKey = `${string}::${MatrixRoleColumn}`; // `${permissionKey}::${column}`

function cellKey(permissionKey: string, column: MatrixRoleColumn): PendingKey {
  return `${permissionKey}::${column}` as PendingKey;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function PermissionsMatrixPage() {
  const [user] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  });

  const [modules, setModules] = useState<PermissionMatrixModule[]>([]);
  const [columns, setColumns] = useState<MatrixRoleColumn[]>([]);
  const [activePreset, setActivePreset] = useState('CONFORMARA_STANDARD');
  const [lockedKeys, setLockedKeys] = useState<Set<string>>(new Set());
  const [serverOverrides, setServerOverrides] = useState<PermissionOverride[]>([]);
  // pending edits not yet saved: cellKey -> new cell value
  const [pending, setPending] = useState<Map<PendingKey, BaselineCell>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = user?.role === 'ADMIN';

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [matrixRes, metaRes] = await Promise.all([
        api.get<PermissionMatrixResponse>('/permissions/matrix'),
        api.get<PermissionMatrixMetaResponse>('/permissions/matrix/meta'),
      ]);
      setModules(matrixRes.data.data.modules);
      setServerOverrides(matrixRes.data.data.overrides || []);
      setActivePreset(matrixRes.data.data.preset || 'CONFORMARA_STANDARD');
      setColumns(metaRes.data.data.columns);
      setLockedKeys(new Set(metaRes.data.data.lockedKeys));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load permission matrix');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) void loadMatrix();
  }, [isAdmin, loadMatrix]);

  // Effective cell = pending edit > server override > baseline.
  const effectiveCell = (permissionKey: string, column: MatrixRoleColumn, baseline: BaselineCell): BaselineCell => {
    const pendingValue = pending.get(cellKey(permissionKey, column));
    if (pendingValue) return pendingValue;
    const override = serverOverrides.find((o) => o.permissionKey === permissionKey && o.role === column);
    if (override) return override.effect === 'ALLOW' ? 'FULL' : 'DENIED';
    return baseline;
  };

  const handleToggle = (permissionKey: string, column: MatrixRoleColumn, current: BaselineCell) => {
    if (lockedKeys.has(permissionKey)) return; // locked — non-waivable invariant
    const next = CELL_CYCLE[current] ?? 'FULL';
    setPending((prev) => {
      const map = new Map(prev);
      map.set(cellKey(permissionKey, column), next);
      return map;
    });
  };

  const handleSave = async () => {
    if (pending.size === 0) return;
    setSaving(true);
    setError(null);
    try {
      // A toggle back to the Standard baseline value revokes the override;
      // anything else persists as an ALLOW/DENY override row.
      for (const [k, value] of pending.entries()) {
        const [permissionKey, column] = k.split('::') as [string, MatrixRoleColumn];
        const row = modules.flatMap((m) => m.rows).find((r) => r.key === permissionKey);
        const baseline = row?.cells[column] ?? 'DENIED';
        if (value === baseline) {
          await api.post('/permissions/overrides/revoke', { permissionKey, role: column });
        } else {
          await api.put('/permissions/overrides', {
            permissionKey,
            role: column,
            effect: value === 'DENIED' ? 'DENY' : 'ALLOW',
            scopeType: value === 'SCOPED' ? 'PROJECT' : 'COMPANY',
          });
        }
      }
      setPending(new Map());
      await loadMatrix();
    } catch (err: any) {
      setError(err?.response?.data?.message || (err?.response?.status === 403 ? 'Only the company Owner/Admin or a grant.delegate holder may save permission overrides' : 'Failed to save overrides'));
    } finally {
      setSaving(false);
    }
  };

  const handleRevertToStandard = async () => {
    if (!confirm('Revert the whole permissions matrix to Conformara Standard? All customizations will be removed.')) return;
    setSaving(true);
    setError(null);
    try {
      await api.post('/permissions/revert-to-standard');
      setPending(new Map());
      await loadMatrix();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to revert to standard');
    } finally {
      setSaving(false);
    }
  };

  // ── Access gate (matrix is company-Admin only, decision 14) ──────────────
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto mt-20 max-w-xl rounded-2xl border border-red-200 bg-white p-8 text-center shadow">
          <Lock className="mx-auto h-12 w-12 text-red-500" />
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Company-admin access required</h1>
          <p className="mt-3 text-gray-600">
            The permissions matrix can only be managed by the company Owner or a delegated Admin.
          </p>
          <Link
            to="/dashboard"
            className="mt-6 inline-block rounded-lg bg-gray-900 px-6 py-2.5 text-white hover:bg-gray-700"
          >
            Return to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-gray-200 bg-white shadow-sm">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-gray-500 hover:text-gray-900">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
                <ShieldCheck className="h-6 w-6 text-blue-600" />
                Permissions Matrix
              </h1>
              <p className="text-sm text-gray-500">
                Active preset: <span className="font-medium text-gray-700">{activePreset}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRevertToStandard}
              disabled={saving}
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RotateCcw className="h-4 w-4" />
              Revert to Standard
            </button>
            <button
              onClick={handleSave}
              disabled={saving || pending.size === 0}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save changes{pending.size > 0 ? ` (${pending.size})` : ''}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] p-6">
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {/* Legend */}
        <div className="mb-4 flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
          <span><b>●</b> Granted</span>
          <span><b>◐</b> Scoped (assigned projects/objects)</span>
          <span><b>D</b> Delegatable</span>
          <span><b>—</b> Denied</span>
          <span className="flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> Non-waivable safety invariant</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-gray-400">
            <Loader2 className="mr-2 h-6 w-6 animate-spin" /> Loading matrix…
          </div>
        ) : (
          modules.map(({ module, rows }) => (
            <section key={module} className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <h2 className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
                {module}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                      <th className="sticky left-0 bg-white px-4 py-2 font-medium">Permission</th>
                      {columns.map((col) => (
                        <th key={col} className="px-3 py-2 text-center font-medium">
                          {COLUMN_LABELS[col]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.key} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                        <td className="sticky left-0 bg-white px-4 py-2 font-medium text-gray-800">
                          <span className="flex items-center gap-1.5">
                            {!row.waivable && <Lock className="h-3.5 w-3.5 text-gray-400" aria-label="Non-waivable" />}
                            {row.label}
                          </span>
                          <span className="block font-mono text-xs text-gray-400">{row.key}</span>
                        </td>
                        {columns.map((col) => {
                          const baseline = row.cells[col];
                          const value = effectiveCell(row.key, col, baseline);
                          const locked = lockedKeys.has(row.key);
                          return (
                            <td key={col} className="px-3 py-2 text-center">
                              <button
                                type="button"
                                disabled={locked}
                                title={locked ? 'Non-waivable safety invariant' : CELL_TITLE[value]}
                                onClick={() => handleToggle(row.key, col, value)}
                                className={[
                                  'inline-flex h-7 w-7 items-center justify-center rounded-md text-base transition',
                                  locked
                                    ? 'cursor-not-allowed text-gray-300'
                                    : 'cursor-pointer hover:bg-blue-50',
                                  value === 'DENIED' ? 'text-gray-300' : value === 'SCOPED' ? 'text-amber-600' : value === 'DELEGATABLE' ? 'text-purple-500' : 'text-blue-600',
                                ].join(' ')}
                              >
                                {CELL_GLYPH[value]}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))
        )}
      </main>
    </div>
  );
}
